import axios, { AxiosError } from "axios";
import { clientCodeHeader } from "../../api/clientApiContext";
import { ApiResponse } from "../../api/types";
import { AnyObject } from "../../types";
import { getBackendBaseUrl } from "../../variables";
import {
  ChartResponse,
  PivotDataResponse,
  RunChartReportRequest,
  RunPivotReportRequest,
  TileDataResponse,
} from "../types/biShared.types";
import {
  AddReportGroupRequest,
  AddReportTemplateRequest,
  AddReportTemplateResponse,
  AnyReportConfigurationForRunning,
  ApiReportResponse,
  ChangeReportGroupRequest,
  ClientsResponse,
  CrossFilterRequest,
  CrossFilterResponse,
  DeleteReportsRequest,
  DeleteReportTemplatesRequest,
  DimensionDictionary,
  DuplicateTemplateRequest,
  GetReportAuthorizationResponse,
  GetReportTemplateForCompanyRequest,
  GetReportTemplateForOrganizationRequest,
  GetReportTemplateResponse,
  GetReportTemplatesResponse,
  GetTemplateFieldsDataRequest,
  NameValidationResponse,
  RenameReportRequest,
  ReorderReportGroupsRequest,
  ReplaceReportGroupRequest,
  ReportAuthorizationInfo,
  ReportGroup,
  ReportGroupsResponse,
  ReportInfo,
  ReportResponse,
  ReportTemplate,
  ReportUsage,
  RunTabularReportRequest,
  RunTileReportRequest,
  TabularDataResponse,
  TemplateInfoFieldsResponse,
  TemplateNameValidationResponse,
  UpdateReportAuthorizationRequest,
  UpdateReportGroupRequest,
  UpdateReportTemplateDataSourceRequest,
  UpdateReportTemplateDataSourceResponse,
  UpdateReportTemplateRequest,
  UpdateReportTemplateResponse,
  UpdateReportTemplateStatusRequest,
  UpdateReportTemplateStatusResponse,
  ValidateReportNameRequest,
  ValidateTemplateNameRequest,
} from "./biClient.types";

export const uiReports = "UiReports";
export const apiReports = "ApiReports";
export const dashboardReports = "DashboardReports";
const usageReports: Record<ReportUsage, string> = {
  Ui: uiReports,
  Api: apiReports,
  Dashboard: dashboardReports,
};
export const uiTabularEndpoint = "UiTabular";
export const apiTabularEndpoint = "ApiTabular";
const dashboardTabularEndpoint = "DashboardTabular";
const uiPivotEndpoint = "UiPivot";
const dashboardPivotEndpoint = "DashboardPivot";
const uiTileEndpoint = "UiTile";
const dashboardTileEndpoint = "DashboardTile";
const uiChartEndpoint = "UiChart";
const dashboardChartEndpoint = "DashboardChart";

const getDataSetHeader = (dataSetId: string) => ({ ["X-Entrilia-BI-DataSet"]: dataSetId });

export interface CancellationToken {
  cancel: () => void;
}
const ReportTemplateGroups = "ReportTemplateGroups";

const baseUri = getBackendBaseUrl("reporting");

const getReportGroupApi = (endpoint: typeof uiReports | typeof ReportTemplateGroups | typeof apiReports) => {
  const addReportGroup = async (request: AddReportGroupRequest) => {
    const { data } = await axios.post<ApiResponse<ReportGroup>>(`${baseUri}/${endpoint}/groups`, request, {
      headers: { ...clientCodeHeader() },
    });
    return data;
  };

  const updateReportGroup = async (request: UpdateReportGroupRequest) => {
    const { data } = await axios.put<ApiResponse<ReportGroup>>(`${baseUri}/${endpoint}/groups`, request, {
      headers: { ...clientCodeHeader() },
    });
    return data;
  };

  const deleteReportGroup = async (clientCode: string, groupId: string) => {
    const { data } = await axios.delete<ApiResponse<ReportGroup>>(
      `${baseUri}/${endpoint}/groups/${clientCode}/${groupId}`,
      {
        headers: { ...clientCodeHeader() },
      }
    );
    return data;
  };

  const replaceReportGroup = async (request: ReplaceReportGroupRequest) => {
    const { data } = await axios.put<ApiResponse<AnyObject>>(`${baseUri}/${endpoint}/groups/replace`, request, {
      headers: { ...clientCodeHeader() },
    });
    return data;
  };

  const reorderReportGroups = async (request: ReorderReportGroupsRequest) => {
    const { data } = await axios.put<ApiResponse<ReportGroupsResponse>>(
      `${baseUri}/${endpoint}/groups/reorder`,
      request,
      {
        headers: { ...clientCodeHeader() },
      }
    );
    return data;
  };

  const getReportGroups = async (clientCode: string) => {
    const { data } = await axios.get<ApiResponse<ReportGroupsResponse>>(`${baseUri}/${endpoint}/groups/${clientCode}`, {
      headers: { ...clientCodeHeader() },
    });
    return data;
  };

  const changeReportGroup = async (request: ChangeReportGroupRequest) => {
    const { data } = await axios.put<ApiResponse<ReportResponse>>(`${baseUri}/${endpoint}/change-group`, request, {
      headers: { ...clientCodeHeader() },
    });
    return data;
  };

  return {
    addReportGroup,
    updateReportGroup,
    deleteReportGroup,
    replaceReportGroup,
    reorderReportGroups,
    getReportGroups,
    changeReportGroup,
  };
};

const getReportsApi = (
  endpoint: typeof uiReports | typeof apiReports | typeof dashboardReports | typeof ReportTemplateGroups
) => {
  const getReportConfigurationForRunning = async (reportId: string, signal?: AbortSignal) => {
    const { data } = await axios.get<ApiResponse<AnyReportConfigurationForRunning>>(
      `${baseUri}/${endpoint}/${reportId}/run-configuration`,
      {
        headers: { ...clientCodeHeader() },
        signal,
      }
    );
    return data;
  };

  const getReports = async (signal?: AbortSignal) => {
    const { data } = await axios.get<ApiResponse<ReportInfo[]>>(`${baseUri}/${endpoint}`, {
      headers: { ...clientCodeHeader() },
      signal,
    });
    return data;
  };

  const renameReport = async (request: RenameReportRequest) => {
    const { data } = await axios.put<ApiResponse<ReportResponse>>(`${baseUri}/${endpoint}/rename`, request, {
      headers: { ...clientCodeHeader() },
    });
    return data;
  };

  const deleteReports = async (reports: DeleteReportsRequest) => {
    const { data } = await axios.patch<ApiResponse<boolean>>(
      `${baseUri}/${endpoint}/delete-reports`,
      { reports },
      {
        headers: { ...clientCodeHeader() },
      }
    );
    return data;
  };

  const validateReportName = async (request: ValidateReportNameRequest) => {
    const { data } = await axios.post<ApiResponse<NameValidationResponse>>(
      `${baseUri}/${endpoint}/validate-report-name`,
      request,
      {
        headers: { ...clientCodeHeader() },
      }
    );
    return data;
  };

  const getReportSharing = async (reportId: string) => {
    const { data } = await axios.get<ApiResponse<GetReportAuthorizationResponse>>(
      `${baseUri}/${endpoint}/${reportId}/sharing`,
      {
        headers: { ...clientCodeHeader() },
      }
    );
    return data;
  };

  const updateReportSharing = async (reportId: string, request: UpdateReportAuthorizationRequest) => {
    const { data } = await axios.put<ApiResponse<ReportAuthorizationInfo>>(
      `${baseUri}/${endpoint}/${reportId}/sharing`,
      request,
      {
        headers: { ...clientCodeHeader() },
      }
    );
    return data;
  };

  const getReportAuthorizationInfo = async (reportId: string) => {
    const { data } = await axios.get<ApiResponse<ReportAuthorizationInfo>>(
      `${baseUri}/${endpoint}/${reportId}/sharing/authorization`,
      {
        headers: { ...clientCodeHeader() },
      }
    );
    return data;
  };

  const runTileReport = (
    dataSetId: string,
    body: RunTileReportRequest,
    done: (data: ApiResponse<TileDataResponse>) => void,
    error: (reason: string | undefined) => void
  ): CancellationToken => {
    if (endpoint === apiReports) {
      throw new Error("Not Supported");
    } else if (endpoint === uiReports) {
      return runTileReportBase(dataSetId, body, done, error, uiTileEndpoint);
    } else if (endpoint === dashboardReports) {
      return runTileReportBase(dataSetId, body, done, error, dashboardTileEndpoint);
    } else {
      throw new Error("Invalid endpoint");
    }
  };

  const runTabularReport = <
    TBody extends RunTabularReportRequest,
    TResponse extends TabularDataResponse | ApiReportResponse,
  >(
    dataSetId: string,
    body: TBody,
    done: (data: ApiResponse<TResponse>) => void,
    error: (reason: string | undefined) => void
  ): CancellationToken => {
    if (endpoint === apiReports) {
      return runTabularReportBase(dataSetId, body, done, error, apiTabularEndpoint);
    } else if (endpoint === uiReports) {
      return runTabularReportBase(dataSetId, body, done, error, uiTabularEndpoint);
    } else if (endpoint === dashboardReports) {
      return runTabularReportBase(dataSetId, body, done, error, dashboardTabularEndpoint);
    } else {
      throw new Error("Invalid endpoint");
    }
  };

  const runPivotReport = (
    dataSetId: string,
    body: RunPivotReportRequest,
    done: (data: ApiResponse<PivotDataResponse>) => void,
    error: (reason: string | undefined) => void
  ): CancellationToken => {
    if (endpoint === apiReports) {
      throw new Error("Not Supported");
    } else if (endpoint === uiReports) {
      return runPivotReportBase(dataSetId, body, done, error, uiPivotEndpoint);
    } else if (endpoint === dashboardReports) {
      return runPivotReportBase(dataSetId, body, done, error, dashboardPivotEndpoint);
    } else {
      throw new Error("Invalid endpoint");
    }
  };

  const runChartReport = (
    dataSetId: string,
    body: RunChartReportRequest,
    done: (data: ApiResponse<ChartResponse>) => void,
    error: (reason: string | undefined) => void
  ): CancellationToken => {
    if (endpoint === apiReports) {
      throw new Error("Not Supported");
    } else if (endpoint === uiReports) {
      return runChartReportBase(dataSetId, body, done, error, uiChartEndpoint);
    } else if (endpoint === dashboardReports) {
      return runChartReportBase(dataSetId, body, done, error, dashboardChartEndpoint);
    } else {
      throw new Error("Invalid endpoint");
    }
  };

  const undraftReports = async (reportIds: string[]) => {
    const { data } = await axios.post<ApiResponse<ReportInfo[]>>(
      `${baseUri}/${endpoint}/undraft-reports`,
      { reportIds },
      {
        headers: { ...clientCodeHeader() },
      }
    );
    return data;
  };

  return {
    getReports,
    renameReport,
    deleteReports,
    validateReportName,
    getReportSharing,
    updateReportSharing,
    getReportAuthorizationInfo,
    runTabularReport,
    runPivotReport,
    runTileReport,
    runChartReport,
    undraftReports,
    getReportConfigurationForRunning,
  };
};

const validateUniqueReportNameByUsage = async (request: ValidateReportNameRequest, usage: ReportUsage) => {
  const endpoint = usageReports[usage];
  const { data } = await axios.post<ApiResponse<NameValidationResponse>>(
    `${baseUri}/${endpoint}/validate-report-name`,
    request,
    {
      headers: { ...clientCodeHeader() },
    }
  );
  return data;
};

const validateTemplateName = async (request: ValidateTemplateNameRequest) => {
  const { data } = await axios.post<ApiResponse<TemplateNameValidationResponse>>(
    `${baseUri}/ReportTemplates/validate-template-name`,
    request,
    {
      headers: { ...clientCodeHeader() },
    }
  );
  return data;
};

const addReportTemplate = async (request: AddReportTemplateRequest) => {
  const { data } = await axios.post<ApiResponse<AddReportTemplateResponse>>(`${baseUri}/ReportTemplates`, request, {
    headers: { ...clientCodeHeader() },
  });
  return data;
};

const getOrganizationReportTemplates = async () => {
  const { data } = await axios.get<ApiResponse<GetReportTemplatesResponse>>(`${baseUri}/ReportTemplates/organization`, {
    headers: { ...clientCodeHeader() },
  });
  return data;
};

const getCompanyReportTemplates = async () => {
  const { data } = await axios.get<ApiResponse<GetReportTemplatesResponse>>(`${baseUri}/ReportTemplates/company`, {
    headers: { ...clientCodeHeader() },
  });
  return data;
};

const getReportTemplateForCompany = async (request: GetReportTemplateForCompanyRequest) => {
  const { data } = await axios.get<ApiResponse<GetReportTemplateResponse>>(
    `${baseUri}/ReportTemplates/${request.templateId}/company`,
    {
      headers: { ...clientCodeHeader() },
    }
  );
  return data;
};

const getReportTemplateForOrganization = async (request: GetReportTemplateForOrganizationRequest) => {
  const { data } = await axios.get<ApiResponse<GetReportTemplateResponse>>(
    `${baseUri}/ReportTemplates/${request.templateId}/${request.organization}/organization`,
    {
      headers: { ...clientCodeHeader() },
    }
  );
  return data;
};

const updateReportTemplate = async (request: UpdateReportTemplateRequest) => {
  const { data } = await axios.put<ApiResponse<UpdateReportTemplateResponse>>(
    `${baseUri}/ReportTemplates/${request.template.reportId}`,
    request,
    {
      headers: { ...clientCodeHeader() },
    }
  );
  return data;
};

const updateReportTemplateStatus = async (request: UpdateReportTemplateStatusRequest) => {
  const { data } = await axios.put<ApiResponse<UpdateReportTemplateStatusResponse>>(
    `${baseUri}/ReportTemplates/${request.templateId}/status`,
    { status: request.status },
    {
      headers: { ...clientCodeHeader() },
    }
  );
  return data;
};

const updateReportTemplateDataSource = async (request: UpdateReportTemplateDataSourceRequest) => {
  const { data } = await axios.put<ApiResponse<UpdateReportTemplateDataSourceResponse>>(
    `${baseUri}/ReportTemplates/${request.templateId}/datasource`,
    { dataSource: request.dataSource },
    {
      headers: { ...clientCodeHeader() },
    }
  );
  return data;
};

const deleteReportTemplates = async (request: DeleteReportTemplatesRequest) => {
  const { data } = await axios.patch<ApiResponse<boolean>>(`${baseUri}/ReportTemplates/bulk-delete`, request, {
    headers: { ...clientCodeHeader() },
  });
  return data;
};

const getClients = async () => {
  const { data } = await axios.get<ApiResponse<ClientsResponse>>(`${baseUri}/Clients`, {
    headers: { ...clientCodeHeader() },
  });
  return data;
};

const getTemplateFieldsData = async (request: GetTemplateFieldsDataRequest) => {
  const { data } = await axios.get<ApiResponse<TemplateInfoFieldsResponse>>(
    `${baseUri}/ReportTemplates/${request.templateId}/info-fields`,
    {
      headers: { ...clientCodeHeader() },
    }
  );
  return data;
};

const duplicateReportTemplate = async (request: DuplicateTemplateRequest) => {
  const { data } = await axios.post<ApiResponse<ReportTemplate | undefined>>(
    `${baseUri}/ReportTemplates/duplicate/${request.templateId}`,
    { name: request.name, groupId: request.groupId, organization: request.organization },
    {
      headers: { ...clientCodeHeader() },
    }
  );
  return data;
};

const runTileReportBase = (
  dataSetId: string,
  body: RunTileReportRequest,
  done: (data: ApiResponse<TileDataResponse>) => void,
  error: (reason: string | undefined) => void,
  endpoint: typeof uiTileEndpoint | typeof dashboardTileEndpoint
): CancellationToken => {
  const controller = new AbortController();
  axios
    .post<ApiResponse<TileDataResponse>>(`${baseUri}/${endpoint}/run`, body, {
      headers: { ...clientCodeHeader(), ...getDataSetHeader(dataSetId) },
      signal: controller.signal,
    })
    .then((res) => done(res.data))
    .catch((reason: Error) => {
      if (reason.message === "canceled") {
        error("canceled");
        return;
      }
      const axiosError = reason as AxiosError<ApiResponse<unknown>>;
      const errorMessage = axiosError.response?.data?.error?.message;
      error(errorMessage);
    });
  return { cancel: () => controller.abort() };
};

const runTabularReportBase = <
  TBody extends RunTabularReportRequest,
  TResponse extends TabularDataResponse | ApiReportResponse,
>(
  dataSetId: string,
  body: TBody,
  done: (data: ApiResponse<TResponse>) => void,
  error: (reason: string | undefined) => void,
  endpoint: typeof uiTabularEndpoint | typeof apiTabularEndpoint | typeof dashboardTabularEndpoint
): CancellationToken => {
  const controller = new AbortController();
  axios
    .post<ApiResponse<TabularDataResponse>>(`${baseUri}/${endpoint}/run`, body, {
      headers: { ...clientCodeHeader(), ...getDataSetHeader(dataSetId) },
      signal: controller.signal,
    })
    .then((res) => done(res.data as ApiResponse<TResponse>))
    .catch((reason: Error) => {
      if (reason.message === "canceled") {
        error("canceled");
        return;
      }
      const axiosError = reason as AxiosError<ApiResponse<unknown>>;
      const errorMessage = axiosError.response?.data?.error?.message;
      error(errorMessage);
    });
  return { cancel: () => controller.abort() };
};

const runPivotReportBase = (
  dataSetId: string,
  body: RunPivotReportRequest,
  done: (data: ApiResponse<PivotDataResponse>) => void,
  error: (reason: string | undefined) => void,
  endpoint: typeof uiPivotEndpoint | typeof dashboardPivotEndpoint
): CancellationToken => {
  const controller = new AbortController();
  axios
    .post<ApiResponse<PivotDataResponse>>(`${baseUri}/${endpoint}/run`, body, {
      headers: { ...clientCodeHeader(), ...getDataSetHeader(dataSetId) },
      signal: controller.signal,
    })
    .then((res) => done(res.data))
    .catch((reason: Error) => {
      if (reason.message === "canceled") {
        error("canceled");
        return;
      }
      const axiosError = reason as AxiosError<ApiResponse<unknown>>;
      const errorMessage = axiosError.response?.data?.error?.message;
      error(errorMessage);
    });
  return { cancel: () => controller.abort() };
};

const runChartReportBase = (
  dataSetId: string,
  body: RunChartReportRequest,
  done: (data: ApiResponse<ChartResponse>) => void,
  error: (reason: string | undefined) => void,
  endpoint: typeof uiChartEndpoint | typeof dashboardChartEndpoint
): CancellationToken => {
  const controller = new AbortController();
  axios
    .post<ApiResponse<ChartResponse>>(`${baseUri}/${endpoint}/run`, body, {
      headers: { ...clientCodeHeader(), ...getDataSetHeader(dataSetId) },
      signal: controller.signal,
    })
    .then((res) => done(res.data))
    .catch((reason: Error) => {
      if (reason.message === "canceled") {
        error("canceled");
        return;
      }
      const axiosError = reason as AxiosError<ApiResponse<unknown>>;
      const errorMessage = axiosError.response?.data?.error?.message;
      error(errorMessage);
    });
  return { cancel: () => controller.abort() };
};

const crossFilter = (
  dataSetId: string,
  body: CrossFilterRequest,
  done: (data: ApiResponse<CrossFilterResponse>) => void,
  error: (reason: string) => void
): CancellationToken => {
  const controller = new AbortController();
  axios
    .post<ApiResponse<CrossFilterResponse>>(`${baseUri}/CrossFiltering`, body, {
      headers: { ...clientCodeHeader(), ...getDataSetHeader(dataSetId) },
      signal: controller.signal,
    })
    .then((res) => done(res.data))
    .catch((reason: Error) => error(reason.message));
  return { cancel: () => controller.abort() };
};

const loadDimensionDictionaries = async (dataSetId: string, ...dimensions: string[]) => {
  const queryParams = new URLSearchParams();
  dimensions.map((d) => queryParams.append("d", d));
  const { data } = await axios.get<ApiResponse<{ [key: string]: DimensionDictionary }[]>>(
    `${baseUri}/MetaData/dictionaries?${queryParams.toString()}`,
    {
      headers: { ...clientCodeHeader(), ...getDataSetHeader(dataSetId) },
    }
  );
  return data;
};

const biClientShared = {
  uiReportGroup: getReportGroupApi(uiReports),
  apiReportGroup: getReportGroupApi(apiReports),
  reportTemplateGroup: getReportGroupApi(ReportTemplateGroups),
  addReportTemplate,
  getOrganizationReportTemplates,
  getCompanyReportTemplates,
  getReportTemplateForCompany,
  getReportTemplateForOrganization,
  updateReportTemplateStatus,
  deleteReportTemplates,
  validateTemplateName,
  updateReportTemplate,
  updateReportTemplateDataSource,
  getClients,
  getTemplateFieldsData,
  duplicateReportTemplate,
  validateUniqueReportNameByUsage,
  uiReports: getReportsApi(uiReports),
  apiReports: getReportsApi(apiReports),
  dashboardReports: getReportsApi(dashboardReports),
  crossFilter,
  loadDimensionDictionaries,
} as const;

export default biClientShared;
